import cn from 'classnames';
import React, { RefObject, useMemo } from 'react';
import cms from 'utils/cms';
import { useClientSelector } from 'client/reducers';
import { selectCmsItems } from 'client/reducers/story/selectors';
import type { BBCommonProps, BBEditableModeProps } from 'types/story';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import css from './Embed.scss';

type Props = BBCommonProps;

/**
 * Embed component for rendering embedded code (like iframes, scripts, etc.)
 *
 * This component takes an embed code from props and renders it using ScriptRenderer
 */
const Embed: React.FC<Props> = props => {
	const { uiConfig, stateAttrs, eventListeners, containerRef, isEditableMode, editableModeProps } = props;
	const cmsItems = useClientSelector(selectCmsItems);

	// Get the embed code from props
	const embedCode = useMemo(() => {
		const value = uiConfig.componentProps?.other?.embedCode ?? '';
		return cms.resolveCmsReferenceValue(value, cmsItems);
	}, [uiConfig.componentProps?.other?.embedCode, cmsItems]);

	// Render the component with the given props
	const renderContent = (additionalProps: Partial<BBEditableModeProps['nodeProps']> = {}) => {
		const { nodeProps } = uiConfig;
		const className = cn(css.embed, nodeProps.className, additionalProps.className, { [css.empty]: !embedCode });
		const isHidden = uiConfig.componentProps.styles?.display === 'none';

		return (
			<div
				{...nodeProps}
				{...stateAttrs}
				{...eventListeners}
				{...additionalProps}
				className={className}
				ref={containerRef as RefObject<HTMLDivElement>}
			>
				{isHidden ? null : <ScriptRenderer value={embedCode} />}
			</div>
		);
	};

	// In editable mode, we need to add additional props from editableModeProps
	if (isEditableMode) {
		const editableProps = { ...editableModeProps?.nodeProps };
		return renderContent(editableProps);
	}

	// Default rendering
	return renderContent();
};

export default withCardTransitionContext(Embed);
